import React from 'react';
import { useField } from 'react-final-form';

import { required as requiredValidator } from '../../util/validators';
import { FieldRadioButton, ValidationError } from '../../components';

import css from './ProviderTypeField.module.css';

const FIELD_KEY = 'provider_type';

/**
 * Renders the provider_type user field as a radio-button group where each
 * option has a short explanatory description below it.
 *
 * Strictly key-scoped: returns null unless fieldConfig.key === 'provider_type'.
 * Does not modify FieldRadioButton or FieldSelect.
 *
 * @component
 * @param {Object} props
 * @param {string} props.name - Final Form field name (e.g. "pub_provider_type")
 * @param {string} [props.formId] - Optional form id prefix
 * @param {Object} props.fieldConfig - The user field configuration from Console
 * @param {string} [props.label] - Group heading label (pass null to suppress)
 * @param {string} props.defaultRequiredMessage - Fallback required message
 * @param {Object} props.intl - react-intl instance
 */
const ProviderTypeField = props => {
  const { name, formId, fieldConfig, label, defaultRequiredMessage, intl } = props;
  // A5-01: the required choice shows its error under the options, tied to every radio (Q2)
  const { meta } = useField(name, { subscription: { touched: true, error: true } });

  if (fieldConfig?.key !== FIELD_KEY) {
    return null;
  }

  const { enumOptions = [], saveConfig } = fieldConfig;
  const { isRequired, required, requiredMessage } = saveConfig || {};
  const validateMaybe =
    isRequired || required
      ? { validate: requiredValidator(requiredMessage || defaultRequiredMessage) }
      : {};

  const showsError = !!(meta.touched && meta.error);
  const errorId = formId ? `${formId}.${name}-error` : `${name}-error`;
  const errorAriaProps = showsError ? { 'aria-invalid': true, 'aria-describedby': errorId } : {};

  const confirmationId = 'ProviderTypeField.statusConfirmation';
  const confirmationText = intl.messages?.[confirmationId]
    ? intl.formatMessage({ id: confirmationId })
    : null;

  return (
    <div className={css.field}>
      {label ? <legend className={css.groupLabel}>{label}</legend> : null}
      {enumOptions.map(o => {
        const optionId = formId ? `${formId}.${name}.${o.option}` : `${name}.${o.option}`;
        const descriptionId = `ProviderTypeField.description.${o.option}`;
        const descriptionText = intl.messages?.[descriptionId]
          ? intl.formatMessage({ id: descriptionId })
          : null;

        return (
          <div key={o.option} className={css.option}>
            <FieldRadioButton
              id={optionId}
              name={name}
              value={o.option}
              label={o.label}
              {...validateMaybe}
              {...errorAriaProps}
            />
            {descriptionText ? (
              <p className={css.optionDescription}>{descriptionText}</p>
            ) : null}
          </div>
        );
      })}
      <ValidationError id={errorId} fieldMeta={meta} />
      {confirmationText ? (
        <p className={css.statusConfirmation}>{confirmationText}</p>
      ) : null}
    </div>
  );
};

export default ProviderTypeField;
